import { useEffect, useState } from 'react';
import type { SetupHandoff, SetupImportReport } from '../renderer/setup-handoff';
import type { PaigeJobProgress } from './paige.global';

/** How often the job's card is read while the setup wizard waits on its documents. */
export const askEvery = 2000;

/**
 * What the setup wizard waits on in Paige (renderer/setup-handoff.ts): the documents setup read,
 * taken in by Paige and processed. CapturePoint's wizard watches the folder import its work screen
 * drains; Paige's setup reads the whole selection itself, so what is left after setup is Paige's
 * own: each document recorded on the job, then checked under the job's processing mode until it is
 * exported or waits for a person. The job's card says where that is (`documents`, `processing`,
 * `pagesReading`), so the wizard counts real documents and lands only when none is still in flight.
 */
export function setupProcessingReport(
  progress: PaigeJobProgress,
  expected: number,
): SetupImportReport {
  const taken = Math.min(progress.documents, expected);
  const done = Math.max(0, Math.min(expected, progress.documents - progress.processing));
  const busy =
    progress.documents < expected || progress.processing > 0 || progress.pagesReading > 0;
  return {
    loaded: true,
    busy,
    failed: false,
    total: expected,
    read: done,
    queuedFiles: Math.max(0, expected - done),
    live: liveLine(progress, taken, expected),
    previewPath: null,
    remainingMilliseconds: null,
    // CapturePoint offers "Ready to check · Start checking" once its import's first batch is in
    // the job; in Paige every document was read by setup, so the first processed one is enough.
    documents: done,
    firstBatchFiles: null,
    firstBatchReady: done > 0,
  };
}

function liveLine(progress: PaigeJobProgress, taken: number, expected: number): string | null {
  if (taken < expected) return `Adding your documents to the job: ${taken} of ${expected}`;
  if (progress.processing > 0)
    return `Checking ${progress.processing} ${progress.processing === 1 ? 'document' : 'documents'}`;
  if (progress.pagesReading > 0) return `Reading ${progress.pagesReading} more pages`;
  return null;
}

/** Nothing to wait on: a workbench outside a Paige job's channel has no card to read. */
const nothingToWaitOn: SetupImportReport = {
  loaded: true,
  busy: false,
  failed: false,
  total: null,
  read: 0,
  queuedFiles: 0,
  live: null,
  previewPath: null,
  remainingMilliseconds: null,
  documents: 0,
  firstBatchFiles: null,
  firstBatchReady: false,
};

/**
 * The report the setup wizard waits on while `held` (the hand-off it holds for) stands: the job's
 * card, read now and every `askEvery` until the hand-off lands. A read that fails is asked again;
 * the wizard keeps its last count and its way to open the job early.
 */
export function useSetupProcessing(
  jobId: string,
  held: SetupHandoff | null,
): SetupImportReport | null {
  const [report, setReport] = useState<{ jobId: string; report: SetupImportReport } | null>(null);
  const expected = held ? held.documents : null;
  const ask = window.paigeWorkbench?.jobProgress;
  useEffect(() => {
    if (expected === null || !ask) return;
    let live = true;
    const tick = async () => {
      const progress = await ask().catch(() => null);
      if (live && progress) setReport({ jobId, report: setupProcessingReport(progress, expected) });
    };
    void tick();
    const timer = window.setInterval(() => void tick(), askEvery);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [jobId, expected, ask]);
  if (expected === null) return null;
  if (!ask) return nothingToWaitOn;
  return report?.jobId === jobId ? report.report : null;
}
